/**
 * Page View Tracking Hook
 */

import { useEffect, useRef } from 'react';
import { useEngageTrack } from './useEngageTrack';

/**
 * Hook to automatically track page views when component mounts or path changes
 * Useful for React Router or Next.js page components
 * 
 * @param path - Optional custom path (defaults to window.location.pathname)
 * @param additionalData - Additional data to include with the page view event
 * 
 * @example
 * ```tsx
 * // In a page component
 * function HomePage() {
 *   usePageView();
 *   
 *   return <div>Home Page</div>;
 * }
 * 
 * // With custom path
 * function ProductPage({ productId }) {
 *   usePageView(`/product/${productId}`, { productId });
 *   
 *   return <div>Product {productId}</div>;
 * }
 * ```
 */
export const usePageView = (
  path?: string,
  additionalData?: Record<string, unknown>
): void => {
  const { track } = useEngageTrack();
  const hasTracked = useRef(false);
  const currentPath = path || (typeof window !== 'undefined' ? window.location.pathname : '');

  useEffect(() => {
    // Only track once per path change
    if (!hasTracked.current) {
      track('page_view', {
        path: currentPath,
        url: typeof window !== 'undefined' ? window.location.href : '',
        title: typeof document !== 'undefined' ? document.title : '',
        ...additionalData,
      });
      hasTracked.current = true;
    }

    // Reset flag when path changes
    return () => {
      hasTracked.current = false;
    };
  }, [currentPath, track, additionalData]);
};
